import React, { FormEvent, useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card } from 'react-bootstrap';
import { CreditCard } from 'lucide-react';

interface PaymentMethodFormProps {
    onSuccess: (paymentMethodId: string) => Promise<void>;
    buttonText?: string;
    className?: string;
}

interface BillingDetails {
    name: string;
    address: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
    };
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
                                                                 onSuccess,
                                                                 buttonText = "Add Payment Method",
                                                                 className = ""
                                                             }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [billingDetails, setBillingDetails] = useState<BillingDetails>({
        name: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US'
        }
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('address.')) {
            const field = name.split('.')[1];
            setBillingDetails(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [field]: value
                }
            }));
        } else {
            setBillingDetails(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        if (!stripe || !elements) {
            setError('Payment system not initialized');
            setLoading(false);
            return;
        }

        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
            setError('Card element not found');
            setLoading(false);
            return;
        }

        const { error: elementError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: billingDetails.name,
                address: billingDetails.address
            }
        });

        if (elementError) {
            setError(elementError.message || 'An error occurred processing your card');
            setLoading(false);
            return;
        }

        if (!paymentMethod) {
            setError('Failed to create payment method');
            setLoading(false);
            return;
        }

        try {
            await onSuccess(paymentMethod.id);
            cardElement.clear();
            // Reset form
            setBillingDetails({
                name: '',
                address: {
                    line1: '',
                    line2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    country: 'US'
                }
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className={`border-0 shadow-sm ${className}`}>
            <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-4">
                    <CreditCard size={24} className="text-primary me-2" />
                    <h6 className="mb-0">Payment Method</h6>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label">Cardholder Name</label>
                        <input
                            type="text"
                            name="name"
                            className="form-control"
                            value={billingDetails.name}
                            onChange={handleInputChange}
                            placeholder="Name on card"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="form-label">Card Details</label>
                        <CardElement
                            className="form-control"
                            options={{
                                style: {
                                    base: {
                                        fontSize: '16px',
                                        color: '#424770',
                                        '::placeholder': {
                                            color: '#aab7c4',
                                        },
                                    },
                                    invalid: {
                                        color: '#9e2146',
                                    },
                                },
                            }}
                        />
                    </div>

                    <h6 className="mb-3">Billing Address</h6>

                    <div className="mb-3">
                        <label className="form-label">Street Address</label>
                        <input
                            type="text"
                            name="address.line1"
                            className="form-control"
                            value={billingDetails.address.line1}
                            onChange={handleInputChange}
                            placeholder="Street address, P.O. box"
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Apt, Suite, Unit (Optional)</label>
                        <input
                            type="text"
                            name="address.line2"
                            className="form-control"
                            value={billingDetails.address.line2}
                            onChange={handleInputChange}
                            placeholder="Apartment, suite, unit, etc."
                        />
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label">City</label>
                            <input
                                type="text"
                                name="address.city"
                                className="form-control"
                                value={billingDetails.address.city}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">State / Province</label>
                            <input
                                type="text"
                                name="address.state"
                                className="form-control"
                                value={billingDetails.address.state}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label">ZIP / Postal Code</label>
                            <input
                                type="text"
                                name="address.postal_code"
                                className="form-control"
                                value={billingDetails.address.postal_code}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Country</label>
                            <select
                                name="address.country"
                                className="form-select"
                                value={billingDetails.address.country}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="US">United States</option>
                                <option value="CA">Canada</option>
                                <option value="GB">United Kingdom</option>
                                <option value="AU">Australia</option>
                                <option value="DE">Germany</option>
                                <option value="FR">France</option>
                                <option value="IT">Italy</option>
                                <option value="ES">Spain</option>
                                <option value="NL">Netherlands</option>
                                <option value="BE">Belgium</option>
                                <option value="AT">Austria</option>
                                <option value="CH">Switzerland</option>
                                <option value="SE">Sweden</option>
                                <option value="NO">Norway</option>
                                <option value="DK">Denmark</option>
                                <option value="FI">Finland</option>
                                <option value="IE">Ireland</option>
                                <option value="NZ">New Zealand</option>
                            </select>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger mb-4" role="alert">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={!stripe || loading}
                    >
                        {loading ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                />
                                Processing...
                            </>
                        ) : (
                            buttonText
                        )}
                    </button>
                </form>
            </Card.Body>
        </Card>
    );
};

export default PaymentMethodForm;